import GoogleAnalytics from './GoogleAnalytics';
import Mixpanel from './Mixpanel';

const delegates = [new GoogleAnalytics(), new Mixpanel()];

const delegateWrapper = (delegateFn, loggerFn) => params => {
  if (__CLIENT__) {
    if (__DEV__) {
      console.groupCollapsed(`[Tracking] ${loggerFn(params)}`);
      console.debug(params);
    }
    for (let delegate of delegates) {
      const fn = delegateFn(delegate);

      if (fn) {
        fn.bind(delegate)(params);
      }
    }
    if (__DEV__) {
      console.groupEnd();
    }
  }
};

export let identifyUser = delegateWrapper(
  delegate => delegate.identifyUser,
  ({ id }) => `Identifying user: ${id}`,
);

export let unidentifyUser = delegateWrapper(
  delegate => delegate.unidentifyUser,
  () => 'Unidentifying user',
);

/*
 * Tracking pageviews is a bit yucky because we need to normalise
 * paths, and we need to make sure we're not doing any duplication
 * due to quirks of our rendering model.'
 */
const normalizePath = path => (path.endsWith('/') ? path : path + '/');

export let trackPageview = (() => {
  let prev = null;

  return ({ pathname, search }) => {
    const location = `${normalizePath(pathname)}${search}`;

    if (location !== prev) {
      prev = location;
      delegateWrapper(
        delegate => delegate.trackPageview,
        ({ location }) => `Pageview: ${location}`,
      )({ location });
    }
  };
})();

export let trackLogin = params => {
  identifyUser(params);
  delegateWrapper(
    delegate => delegate.trackLogin,
    ({ id, username, backend }) => `Login: ${backend} - ${username} (${id})`,
  )(params);
};

export let trackFailedLogin = delegateWrapper(
  delegate => delegate.trackFailedLogin,
  ({ backend, reason }) => `Failed login: ${backend} - ${reason}`,
);

export let trackLogout = params => {
  unidentifyUser(params);
  delegateWrapper(delegate => delegate.trackLogout, () => 'Logout')(params);
};

export let trackBuyProduct = delegateWrapper(
  delegate => delegate.trackBuyProduct,
  ({ id }) => `Buy Intent: ${id}`,
);

type trackLikeProductParams = (params: {
  id: number,
  username: string,
  slug: string,
  price: string,
  currency: string,
  source: string,
  similarItemIndex: number,
}) => void;

export let trackLikeProduct: trackLikeProductParams = delegateWrapper(
  delegate => delegate.trackLikeProduct,
  ({ id }) => `Like: ${id}`,
);

type trackUnlikeProductParams = (params: {
  id: number,
  username: string,
  slug: string,
  price: string,
  currency: string,
  source: string,
  similarItemIndex: number,
}) => void;

export let trackUnlikeProduct: trackUnlikeProductParams = delegateWrapper(
  delegate => delegate.trackUnlikeProduct,
  ({ id }) => `Unlike: ${id}`,
);

export let trackFollowUser = delegateWrapper(
  delegate => delegate.trackFollowUser,
  ({ id }) => `Follow: ${id}`,
);

export let trackUnfollowUser = delegateWrapper(
  delegate => delegate.trackUnfollowUser,
  ({ id }) => `Unfollow: ${id}`,
);

export let trackProductSearch = delegateWrapper(
  delegate => delegate.trackProductSearch,
  ({ query }) => `Product Search: ${query}`,
);

export let trackPasswordResetRequest = delegateWrapper(
  delegate => delegate.trackPasswordResetRequest,
  ({ identifier }) => `Password Reset Request: ${identifier}`,
);

export let trackProductCardClick = delegateWrapper(
  delegate => delegate.trackProductCardClick,
  ({ id, index, listName }) =>
    `Product Card Click: id=${id} index=${index} list=${listName}`,
);

export let trackLanguageChange = delegateWrapper(
  delegate => delegate.trackLanguageChange,
  ({ language }) => `Language changed to ${language}`,
);

export let trackOpenFeedback = delegateWrapper(
  delegate => delegate.trackOpenFeedback,
  ({ id }) => `Feedback modal opened: ${id}`,
);

export let trackOpenFollowers = delegateWrapper(
  delegate => delegate.trackOpenFollowers,
  ({ id }) => `Followers modal opened: ${id}`,
);

export let trackOpenFollowing = delegateWrapper(
  delegate => delegate.trackOpenFollowing,
  ({ id }) => `Following modal opened: ${id}`,
);

export let trackEditShop = delegateWrapper(
  delegate => delegate.trackEditShop,
  () => `Shop edit mode activated`,
);

export let trackSaveEditShop = delegateWrapper(
  delegate => delegate.trackSaveEditShop,
  () => `Shop edits saved`,
);

export let trackCancelEditShop = delegateWrapper(
  delegate => delegate.trackCancelEditShop,
  () => `Shop edit cancelled`,
);

export let trackMoveSoldItemsToBottom = delegateWrapper(
  delegate => delegate.moveSoldItemsToBottom,
  () => `Move sold items in shop to bottom`,
);

export let trackCropperOpen = delegateWrapper(
  delegate => delegate.trackCropperOpen,
  () => `Cropper opened`,
);

export let trackCropperSave = delegateWrapper(
  delegate => delegate.trackCropperSave,
  () => `Cropper saved`,
);

export let trackBeginListingFlow = delegateWrapper(
  delegate => delegate.trackBeginListingFlow,
  () => `Begin listing flow`,
);

export let trackConfirmListing = delegateWrapper(
  delegate => delegate.trackConfirmListing,
  () => `Confirm listing`,
);

export let trackConfirmListingAndAddAnother = delegateWrapper(
  delegate => delegate.trackConfirmListingAndAddAnother,
  () => `Confirm listing and add another`,
);

export let trackDownLoadAppHeader = delegateWrapper(
  delegate => delegate.trackDownLoadAppHeader,
  ({ source }) => `Download App clicked from ${source}`,
);

export let trackShowAB = delegateWrapper(
  delegate => delegate.trackShowAB,
  ({ testId, variantId }) => `web experiment started testId: ${testId}, variantId: ${variantId}`,
);

export let trackActionAB = delegateWrapper(
  delegate => delegate.trackActionAB,
  ({ testId, variantId, button }) => `web experiment action testId: ${testId}, variantId: ${variantId}, button: ${button}`,
);

export let trackProfileGetTheApp = delegateWrapper(
  delegate => delegate.trackProfileGetTheApp,
  ({ username }) => `profile banner clicked from user profile: ${username}`,
);

export let trackVideoPlay = delegateWrapper(
  delegate => delegate.trackVideoPlay,
  () => 'product play video',
);



// WEBPACK FOOTER //
// ../src/client/utils/tracking/index.js